import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api.js';
import { useSession } from '../App.js';
import type { OptionCardView, SavedSetup } from '../api.js';
import { GameSetupFields } from '../components/GameSetupFields.js';
import { settingsLine } from '../components/GameSettings.js';
import { DEFAULT_SETUP, settingsOfSetup, setupFrom } from '../gameSetup.js';
import type { GameSetup } from '../gameSetup.js';
import { Account } from './Account.js';

/**
 * A player's saved game setups, from the account panel.
 *
 * Each is every New Game setting but the course, edited with the same
 * questions New Game asks. The default is what New Game opens with; the
 * others are a pick away there.
 */
export function Setups() {
  const navigate = useNavigate();
  const { me } = useSession();
  /** The account panel, which this page is reached from. */
  const [profileOpen, setProfileOpen] = useState(false);
  const [setups, setSetups] = useState<SavedSetup[] | null>(null);
  const [catalog, setCatalog] = useState<OptionCardView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The setup being edited: an id, or 'new'. Null when none is open. */
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<GameSetup>({ ...DEFAULT_SETUP });

  const load = useCallback(async () => {
    try {
      setSetups((await api.setups()).setups);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your setups.');
    }
  }, []);

  useEffect(() => {
    void load();
    void api.optionCatalog()
      .then((o) => setCatalog([...o.options].sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, [load]);

  const open = (setup: SavedSetup | null) => {
    setError(null);
    setEditing(setup ? setup.id : 'new');
    setName(setup?.name ?? '');
    setDraft(setup ? setupFrom(setup.settings) : { ...DEFAULT_SETUP });
  };

  const run = async (work: () => Promise<unknown>, failed: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : failed);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const ok = await run(
      () => (editing === 'new'
        ? api.createSetup(name, draft)
        : api.updateSetup(editing as number, name, draft)),
      'Could not save the setup.',
    );
    if (ok) setEditing(null);
  };

  return (
    <main className="content stack">
      <div className="spread">
        <div>
          <p className="eyebrow">Your account</p>
          <h2>Game setups</h2>
        </div>
        <div className="row" style={{ gap: '0.4rem' }}>
          <button className="secondary" onClick={() => setProfileOpen(true)}>Profile</button>
          <button className="secondary" onClick={() => navigate('/')}>Lobby</button>
        </div>
      </div>

      <p className="muted">
        Every New Game setting but the course. New Game opens with your
        default.
      </p>

      {error && <p className="notice">{error}</p>}

      {setups === null && <p className="eyebrow">Loading</p>}
      {setups?.length === 0 && editing === null && (
        <div className="empty"><p className="muted">No setups saved yet.</p></div>
      )}

      <div className="stack" style={{ gap: '0.5rem' }}>
        {setups?.map((s) => (
          <div key={s.id} className="result-row">
            <span>
              <span className="display" style={{ fontSize: '1.1rem' }}>{s.name}</span>
              {s.isDefault && <span className="tag lobby setup-default">Default</span>}
              <span className="mono muted" style={{ display: 'block', fontSize: '0.75rem' }}>
                {settingsLine(settingsOfSetup(setupFrom(s.settings), catalog))}
              </span>
            </span>
            <span className="row setup-actions">
              <button className="secondary tiny" disabled={busy} onClick={() => open(s)}>Edit</button>
              {!s.isDefault && (
                <button
                  className="secondary tiny"
                  disabled={busy}
                  onClick={() => void run(
                    () => api.setDefaultSetup(s.id, true), 'Could not change the default.',
                  )}
                >
                  Make default
                </button>
              )}
              <button
                className="danger tiny"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm(`Delete the setup "${s.name}"?`)) return;
                  if (editing === s.id) setEditing(null);
                  void run(() => api.deleteSetup(s.id), 'Could not delete the setup.');
                }}
              >
                Delete
              </button>
            </span>
          </div>
        ))}
      </div>

      {editing === null ? (
        <div className="row">
          <button disabled={busy || setups === null} onClick={() => open(null)}>New setup</button>
        </div>
      ) : (
        <section className="stack">
          <p className="eyebrow">{editing === 'new' ? 'New setup' : 'Editing'}</p>
          <div className="settings">
            <div>
              <label htmlFor="setup-name">Setup name</label>
              <input
                id="setup-name"
                value={name}
                maxLength={64}
                onChange={(e) => setName(e.target.value)}
                placeholder="Required"
              />
            </div>
            <GameSetupFields value={draft} onChange={setDraft} catalog={catalog} />
          </div>
          <div className="row">
            <button disabled={busy || !name.trim()} onClick={() => void save()}>Save</button>
            <button className="secondary" disabled={busy} onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </section>
      )}
      {profileOpen && me && (
        <Account username={me.username} onClose={() => setProfileOpen(false)} />
      )}
    </main>
  );
}
